import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, Save, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiGetSectionMedia, apiSaveSectionMedia, apiUploadImage } from "../adminApi";
import { computeBadgeLayout, DEVICE_SCALE } from "@/lib/checkLayout";

const DEVICES = [
  { id: "phone", label: "Telefono" },
  { id: "smartwatch", label: "Smartwatch" },
  { id: "pc", label: "PC" },
  { id: "camera", label: "Fotocamera" },
  { id: "console", label: "Console" },
];
const GRADES = [
  { key: "like_new", label: "Come nuovo" },
  { key: "used", label: "Usato" },
];
const N_POINTS = 8;

// Etichette fisse dei controlli, nello stesso ordine mostrato sul sito (HowWeCheck).
// Servono per capire a quale componente corrisponde ogni punto durante il posizionamento.
const CHECK_LABELS = {
  phone: ["Display e touch", "Fotocamere", "Salute batteria", "Face ID e impronta", "Connettività", "Audio e microfoni", "Tasti e ricarica", "Estetica e ripristino"],
  smartwatch: ["Display e sempre-attivo", "Sensori di salute", "Corona e tasti", "Batteria e ricarica", "Cinturino e aggancio", "Resistenza all'acqua", "Connettività", "GPS e ripristino"],
  pc: ["Display e pixel", "Tastiera e trackpad", "Batteria e cicli", "Prestazioni", "Archiviazione SSD", "Porte e connettori", "Webcam e audio", "Ventole e ripristino"],
  camera: ["Sensore e immagine", "Otturatore e scatti", "Autofocus", "Mirino e schermo", "Ghiere e pulsanti", "Innesto e obiettivo", "Schede e batteria", "Connessioni e firmware"],
  console: ["Accensione e alimentazione", "Lettore dischi", "Uscita video", "Porte e connettori", "Raffreddamento", "Controller: stick", "Tasti e grilletti", "Storage e ripristino"],
};

const defaultPoint = (i) => ({ x: i < 4 ? 32 : 68, y: 14 + (i % 4) * 24 });

function useUpload() {
  const [busy, setBusy] = useState(false);
  const run = async (file, cb) => {
    setBusy(true);
    try {
      const res = await apiUploadImage(file, "rinova/sections", undefined, "");
      cb(res.url);
      toast.success("File caricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

function GradeCard({ grade, data, onChange }) {
  const inputRef = useRef(null);
  const [busy, run] = useUpload();
  const d = data || {};
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface2 p-4">
      <div className="relative flex aspect-[240/190] items-center justify-center overflow-hidden rounded-xl" style={{ background: grade.key === "like_new" ? "#151A0C" : "#12140E" }}>
        {d.image ? (
          <img src={d.image} alt="" className="max-h-full max-w-full object-contain" style={{ transform: `scale(${d.scale || 1})` }} />
        ) : (
          <span className="text-sm text-tertiary">Nessuna immagine (resta il design attuale)</span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-chalk">{grade.label}</span>
        <div className="flex items-center gap-1.5">
          <input ref={inputRef} type="file" accept="image/png,image/webp,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(f, (url) => onChange({ ...d, image: url })); }} data-testid={`grade-input-${grade.key}`} />
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`grade-upload-${grade.key}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {d.image ? "Sostituisci" : "Carica PNG"}
          </Button>
          {d.image && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onChange({ ...d, image: "" })}><X className="h-4 w-4" /></Button>}
        </div>
      </div>
      <div className={d.image ? "" : "pointer-events-none opacity-40"}>
        <div className="mb-1 flex items-center justify-between"><Label className="text-xs text-steel">Dimensione</Label><span className="text-xs text-tertiary">{Math.round((d.scale || 1) * 100)}%</span></div>
        <Slider value={[d.scale || 1]} min={0.5} max={1.6} step={0.05} onValueChange={(v) => onChange({ ...d, scale: v[0] })} data-testid={`grade-scale-${grade.key}`} />
      </div>
    </div>
  );
}

function DeviceEditor({ device, data, onChange }) {
  const inputRef = useRef(null);
  const stageRef = useRef(null);
  const [busy, run] = useUpload();
  const [drag, setDrag] = useState(null); // { i, kind: "point" | "badge" }
  const [active, setActive] = useState(null);
  const d = data || {};
  const points = d.points || {};
  const labels = CHECK_LABELS[device.id] || [];
  const clamp = (v) => Math.max(0, Math.min(100, v));

  const baseXY = (i) => points[i] || points[String(i)] || defaultPoint(i);
  const ptsArr = Array.from({ length: N_POINTS }).map((_, i) => { const b = baseXY(i); return { x: b.x, y: b.y }; });
  const badgeDefaults = computeBadgeLayout(ptsArr);
  const getX = (i) => baseXY(i).x;
  const getY = (i) => baseXY(i).y;
  const getBx = (i) => { const b = baseXY(i); return b.bx != null ? b.bx : badgeDefaults[i].bx; };
  const getBy = (i) => { const b = baseXY(i); return b.by != null ? b.by : badgeDefaults[i].by; };

  const update = (i, patch) => {
    const b = baseXY(i);
    const cur = { x: b.x, y: b.y, bx: b.bx != null ? b.bx : badgeDefaults[i].bx, by: b.by != null ? b.by : badgeDefaults[i].by };
    const m = { ...cur, ...patch };
    onChange({ ...d, points: { ...points, [i]: { x: clamp(m.x), y: clamp(m.y), bx: clamp(m.bx), by: clamp(m.by) } } });
  };

  const onPointerMove = (e) => {
    if (!drag || !stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    if (drag.kind === "point") update(drag.i, { x, y });
    else update(drag.i, { bx: x, by: y });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-steel">Carica il PNG del dispositivo. <b>Trascina il numero</b> per spostare l'etichetta e <b>trascina il pallino lime</b> alla fine della freccia per puntare la componente esatta: così regoli lunghezza e direzione di ogni freccia.</p>
        <div className="flex items-center gap-1.5">
          <input ref={inputRef} type="file" accept="image/png,image/webp,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(f, (url) => onChange({ ...d, image: url })); }} data-testid={`device-input-${device.id}`} />
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`device-upload-${device.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {d.image ? "Sostituisci PNG" : "Carica PNG"}
          </Button>
          {d.image && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onChange({ ...d, image: "" })}><X className="h-4 w-4" /></Button>}
        </div>
      </div>

      <div
        ref={stageRef}
        className="relative mx-auto aspect-[320/256] w-full max-w-[600px] touch-none select-none overflow-hidden rounded-2xl bg-surface2"
        style={{ backgroundImage: "radial-gradient(circle at 50% 45%, rgba(198,242,78,0.06), transparent 68%)" }}
        onPointerMove={onPointerMove}
        onPointerUp={() => setDrag(null)}
        onPointerLeave={() => setDrag(null)}
        data-testid={`device-stage-${device.id}`}
      >
        {d.image ? (
          <img src={d.image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-contain" style={{ transform: `scale(${DEVICE_SCALE})`, transformOrigin: "center" }} />
        ) : (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-tertiary">Carica un PNG per iniziare (i punti restano trascinabili)</div>
        )}

        {/* Frecce lime (numero → componente) */}
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {Array.from({ length: N_POINTS }).map((_, i) => {
            const on = active === i || (drag && drag.i === i);
            return (
              <line
                key={i}
                x1={getBx(i)} y1={getBy(i)} x2={getX(i)} y2={getY(i)}
                stroke="#C6F24E" strokeOpacity={on ? 1 : 0.55} strokeWidth={on ? 2 : 1.4}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>

        {/* Pallini finali (punta della freccia sulla componente) */}
        {Array.from({ length: N_POINTS }).map((_, i) => {
          const on = active === i || (drag && drag.i === i);
          return (
            <button
              key={`pt-${i}`}
              type="button"
              onPointerDown={(e) => { e.preventDefault(); setDrag({ i, kind: "point" }); setActive(i); }}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive((a) => (a === i && !drag ? null : a))}
              className={["absolute -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full ring-2 ring-ink transition-transform duration-150 active:cursor-grabbing", on ? "h-4 w-4 scale-110 bg-lime" : "h-3 w-3 bg-lime/80"].join(" ")}
              style={{ left: `${getX(i)}%`, top: `${getY(i)}%`, zIndex: on ? 30 : 15 }}
              data-testid={`device-endpoint-${device.id}-${i}`}
              aria-label={`Punta freccia ${i + 1}`}
            />
          );
        })}

        {/* Numeri (etichette trascinabili) */}
        {Array.from({ length: N_POINTS }).map((_, i) => {
          const on = active === i || (drag && drag.i === i);
          return (
            <div key={`bd-${i}`} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${getBx(i)}%`, top: `${getBy(i)}%`, zIndex: on ? 40 : 20 }}>
              {on && labels[i] && (
                <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-1 text-[11px] font-semibold text-lime ring-1 ring-lime/40 shadow-lg">
                  {labels[i]}
                </span>
              )}
              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); setDrag({ i, kind: "badge" }); setActive(i); }}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive((a) => (a === i && !drag ? null : a))}
                className={["flex h-7 w-7 cursor-grab items-center justify-center rounded-full text-[12px] font-bold shadow-md transition-transform duration-150 active:cursor-grabbing", on ? "scale-110 bg-lime text-ink ring-2 ring-lime" : "bg-ink text-chalk ring-1 ring-lime/50"].join(" ")}
                data-testid={`device-point-${device.id}-${i}`}
              >
                {i + 1}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-tertiary"><Move className="mr-1 inline h-3 w-3" /> Trascina il <b className="text-lime">numero</b> o il <b className="text-lime">pallino</b> della freccia per posizionarli con precisione.</p>

      {/* Legenda numero → controllo */}
      <div className="mt-1 rounded-xl border border-line bg-surface2 p-3" data-testid={`device-legend-${device.id}`}>
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-steel">Legenda controlli</p>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {labels.map((label, i) => {
            const on = active === i;
            return (
              <div
                key={i}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive((a) => (a === i ? null : a))}
                className={["flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150", on ? "bg-lime/10" : "hover:bg-white/[0.04]"].join(" ")}
                data-testid={`device-legend-item-${device.id}-${i}`}
              >
                <span className={["flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold", on ? "bg-lime text-ink" : "bg-ink text-chalk ring-1 ring-chalk/20"].join(" ")}>{i + 1}</span>
                <span className={["text-[13px] leading-tight", on ? "text-chalk" : "text-steel"].join(" ")}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function MediaSections() {
  const [grades, setGrades] = useState(null);
  const [devices, setDevices] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGetSectionMedia().then((d) => { setGrades(d.grades || {}); setDevices(d.devices || {}); }).catch(() => { setGrades({}); setDevices({}); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiSaveSectionMedia(grades, devices);
      toast.success("Media salvati e pubblicati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  if (grades === null || devices === null) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-tertiary" /></div>;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="media-sections">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-chalk">Media sezioni</h1>
          <p className="mt-1 max-w-2xl text-sm text-steel">Sostituisci le illustrazioni con PNG realistici e posiziona manualmente i punti dei controlli.</p>
        </div>
        <Button variant="lime" size="sm" onClick={save} disabled={saving} data-testid="media-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva e pubblica
        </Button>
      </div>

      <Tabs defaultValue="grades">
        <TabsList>
          <TabsTrigger value="grades" data-testid="media-tab-grades">Come nuovo / Usato</TabsTrigger>
          <TabsTrigger value="check" data-testid="media-tab-check">Controlli (device)</TabsTrigger>
        </TabsList>

        <TabsContent value="grades" className="mt-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {GRADES.map((g) => (
              <GradeCard key={g.key} grade={g} data={grades[g.key]} onChange={(v) => setGrades((prev) => ({ ...prev, [g.key]: v }))} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="check" className="mt-5">
          <Tabs defaultValue={DEVICES[0].id}>
            <TabsList>
              {DEVICES.map((dv) => <TabsTrigger key={dv.id} value={dv.id} data-testid={`media-device-tab-${dv.id}`}>{dv.label}</TabsTrigger>)}
            </TabsList>
            {DEVICES.map((dv) => (
              <TabsContent key={dv.id} value={dv.id} className="mt-5">
                <DeviceEditor device={dv} data={devices[dv.id]} onChange={(v) => setDevices((prev) => ({ ...prev, [dv.id]: v }))} />
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}
